import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Send, Instagram } from 'lucide-react';
import { CONTACT_INFO } from '../mock';
import { useBooking } from '../context/BookingContext';

const Contact = () => {
  const { openBooking } = useBooking();
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', sms_opt_in: false });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // SMS opt-in requires a phone number to be meaningful + carrier-compliant.
    if (form.sms_opt_in && !form.phone.trim()) {
      alert('Please add your mobile number to receive SMS messages, or untick the SMS opt-in.');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to send');
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '', message: '', sms_opt_in: false });
    } catch {
      alert('Sorry — we couldn\'t send your message. Please try again or call us directly.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-px bg-rose-400" />
            <span className="text-rose-400 text-xs tracking-[0.4em] uppercase font-light">Get In Touch</span>
            <div className="w-8 h-px bg-rose-400" />
          </div>
          <h2
            className="text-stone-800 mb-4"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 4vw, 3rem)' }}
          >
            Visit Us Today
          </h2>
          <p className="text-stone-400 max-w-lg mx-auto font-light text-lg">
            We would love to welcome you. Schedule a visit or drop us a message.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">

          {/* Left: Info */}
          <div
            className={`transition-all duration-1000 delay-200 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            {/* Contact details */}
            <div className="space-y-6 mb-10">
              {[
                {
                  icon: MapPin,
                  title: 'Location',
                  lines: [CONTACT_INFO.address, CONTACT_INFO.city]
                },
                {
                  icon: Phone,
                  title: 'Phone',
                  lines: [CONTACT_INFO.phone]
                }
              ].map((item, i) => (
                <div key={i} className="flex gap-5">
                  <div className="w-10 h-10 bg-rose-50 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4 h-4 text-rose-400" />
                  </div>
                  <div>
                    <div className="text-stone-700 text-sm font-medium mb-1">{item.title}</div>
                    {item.lines.map((line, idx) => (
                      <div key={idx} className="text-stone-400 text-sm font-light">{line}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Hours */}
            <div className="bg-stone-50 p-6 mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-rose-400" />
                <span className="text-stone-700 text-sm font-medium tracking-wide">Business Hours</span>
              </div>
              <div className="space-y-2">
                {CONTACT_INFO.hours.map((h, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-stone-500 font-light">{h.day}</span>
                    <span className={`font-medium ${h.time === 'Closed' ? 'text-stone-400' : 'text-stone-700'}`}>
                      {h.time}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Social */}
            <div>
              <div className="text-stone-700 text-sm font-medium mb-3 tracking-wide">Follow Us</div>
              <div className="flex gap-3">
                <a
                  href={CONTACT_INFO.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 border border-stone-200 hover:border-rose-300 hover:bg-rose-50 text-stone-500 hover:text-rose-400 text-xs tracking-widest uppercase px-4 py-2 transition-all duration-300"
                >
                  <Instagram className="w-3.5 h-3.5" />
                  Instagram
                </a>
                <a
                  href={CONTACT_INFO.social.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 border border-stone-200 hover:border-rose-300 hover:bg-rose-50 text-stone-500 hover:text-rose-400 text-xs tracking-widest uppercase px-4 py-2 transition-all duration-300"
                >
                  TikTok
                </a>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div
            className={`transition-all duration-1000 delay-400 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mb-6">
                  <Send className="w-6 h-6 text-rose-400" />
                </div>
                <h3
                  className="text-stone-800 text-2xl mb-3"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Message Sent!
                </h3>
                <p className="text-stone-400 font-light mb-6">
                  Thank you for reaching out. We will be in touch within 24 hours.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="border border-stone-200 text-stone-500 text-xs tracking-widest uppercase px-6 py-2 hover:border-rose-300 hover:text-rose-400 transition-colors duration-300"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-stone-600 text-xs tracking-widest uppercase block mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="w-full border border-stone-200 px-4 py-3 text-stone-700 text-sm placeholder-stone-300 focus:outline-none focus:border-rose-300 transition-colors duration-300 bg-white"
                    />
                  </div>
                  <div>
                    <label className="text-stone-600 text-xs tracking-widest uppercase block mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="(123) 456-7890"
                      className="w-full border border-stone-200 px-4 py-3 text-stone-700 text-sm placeholder-stone-300 focus:outline-none focus:border-rose-300 transition-colors duration-300 bg-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-stone-600 text-xs tracking-widest uppercase block mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="w-full border border-stone-200 px-4 py-3 text-stone-700 text-sm placeholder-stone-300 focus:outline-none focus:border-rose-300 transition-colors duration-300 bg-white"
                  />
                </div>
                <div>
                  <label className="text-stone-600 text-xs tracking-widest uppercase block mb-2">Message</label>
                  <textarea
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your goals or questions..."
                    className="w-full border border-stone-200 px-4 py-3 text-stone-700 text-sm placeholder-stone-300 focus:outline-none focus:border-rose-300 transition-colors duration-300 bg-white resize-none"
                  />
                </div>

                {/* SMS opt-in — carrier/TCPA compliant consent block */}
                <label
                  htmlFor="sms_opt_in"
                  data-testid="contact-sms-optin-label"
                  className="flex items-start gap-3 bg-stone-50 border border-stone-200 px-4 py-3.5 cursor-pointer hover:border-rose-300 transition-colors"
                >
                  <input
                    type="checkbox"
                    id="sms_opt_in"
                    name="sms_opt_in"
                    checked={form.sms_opt_in}
                    onChange={handleChange}
                    data-testid="contact-sms-optin-checkbox"
                    className="mt-0.5 w-4 h-4 accent-rose-400 flex-shrink-0 cursor-pointer"
                  />
                  <span className="text-stone-600 text-xs leading-relaxed">
                    <span className="block text-stone-800 font-medium mb-1">
                      Yes, send me SMS updates from Akira Signature Care.
                    </span>
                    By checking this box and providing your mobile number, you agree to receive SMS messages from Akira Signature Care for customer care, account notifications, and marketing. Message frequency varies. Message and data rates may apply. Reply <strong>STOP</strong> to opt out, <strong>HELP</strong> for help. See our{' '}
                    <Link to="/privacy" className="underline text-rose-500 hover:text-rose-600">
                      Privacy Policy
                    </Link>{' '}
                    and{' '}
                    <Link to="/terms" className="underline text-rose-500 hover:text-rose-600">
                      Terms
                    </Link>.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-stone-800 hover:bg-rose-400 text-white text-sm tracking-widest uppercase py-4 transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {sending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
                <p className="text-stone-300 text-xs text-center">
                  Or{' '}
                  <button
                    type="button"
                    onClick={() => openBooking()}
                    className="text-rose-400 hover:underline"
                  >
                    book an appointment directly
                  </button>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
